// src/pages/RoutesPlanning.js
import React, { useEffect, useState, useRef } from "react";
import { db, collection, getDocs, addDoc, updateDoc, doc, deleteDoc } from "../firebase";
import LoadingSpinner from "../components/LoadingSpinner";
import ConfirmModal from "../components/ConfirmModal";
import sadcCountries from "../data/sadc_countries.json";

const FeedbackModal = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => onClose(), 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: "400px", padding: "20px" }}>
        <h3>{type === "success" ? "Success" : "Error"}</h3>
        <p style={{ color: type === "success" ? "#15803d" : "#b91c1c" }}>{message}</p>
        <div className="modal-actions">
          <button className="btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// View Border Posts Modal
const ViewBorderPostsModal = ({ borderPosts, onClose }) => {
  const modalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  return (
    <div className="modal-overlay">
      <div className="modal" ref={modalRef} style={{ maxWidth: "500px" }}>
        <h3>Border Posts</h3>
        {borderPosts.length === 0 ? (
          <p style={{ textAlign: "center", padding: "20px", color: "#94a3b8" }}>
            No border posts assigned
          </p>
        ) : (
          <div className="border-posts-view">
            {borderPosts.map((post, idx) => (
              <div key={idx} className="border-post-view-item">
                <span className="border-order">{idx + 1}.</span>
                <span className="border-name">{post}</span>
              </div>
            ))}
          </div>
        )}
        <div className="modal-actions">
          <button className="btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// View Agents Modal
const ViewAgentsModal = ({ agents, onClose }) => {
  const modalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  return (
    <div className="modal-overlay">
      <div className="modal" ref={modalRef} style={{ maxWidth: "500px" }}>
        <h3>Assigned Agents</h3>
        {agents.length === 0 ? (
          <p style={{ textAlign: "center", padding: "20px", color: "#94a3b8" }}>
            No agents assigned
          </p>
        ) : (
          <div className="agents-view">
            {agents.map((agent, idx) => (
              <div key={idx} className="agent-view-item">
                <span className="agent-icon">👤</span>
                <span className="agent-name">{agent}</span>
              </div>
            ))}
          </div>
        )}
        <div className="modal-actions">
          <button className="btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Cost Template Modal
const CostTemplateModal = ({ route, costTemplates, onAttach, onClose }) => {
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [attachedTemplate, setAttachedTemplate] = useState(route.cost_template || null);
  const modalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  const handleAttach = () => {
    if (selectedTemplate) {
      onAttach(selectedTemplate);
      setAttachedTemplate(selectedTemplate);
      setSelectedTemplate("");
    }
  };

  const handleRemove = () => {
    onAttach(null);
    setAttachedTemplate(null);
  };

  return (
    <div className="modal-overlay">
      <div className="modal" ref={modalRef} style={{ maxWidth: "500px" }}>
        <h3>Cost Template for {route.name}</h3>
        
        {attachedTemplate ? (
          <div className="attached-template">
            <div className="template-badge">
              📋 Currently attached: <strong>{attachedTemplate}</strong>
            </div>
            <button className="btn-delete-small" onClick={handleRemove}>
              Remove Template
            </button>
          </div>
        ) : (
          <p style={{ color: "#64748b", marginBottom: "16px" }}>
            No cost template attached to this route.
          </p>
        )}

        <div className="template-selector">
          <select
            value={selectedTemplate}
            onChange={(e) => setSelectedTemplate(e.target.value)}
            className="template-select"
          >
            <option value="">-- Select a Cost Template --</option>
            {costTemplates.map((template) => (
              <option key={template.id} value={template.name}>
                {template.name} (${template.totalCostDefault?.toFixed(2) || "0"})
              </option>
            ))}
          </select>
          <button
            className="btn-primary"
            onClick={handleAttach}
            disabled={!selectedTemplate}
          >
            Attach Template
          </button>
        </div>

        <div className="modal-actions">
          <button className="btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const ActionModal = ({ item, type, onEdit, onDelete, onView, onDuplicate, onAssign, onUnassign, onCostTemplate, onClose, hasActiveTrips }) => {
  const modalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  return (
    <div className="modal-overlay">
      <div className="modal" ref={modalRef}>
        <h3>Actions for {type === "route" ? item.name : type === "border" ? "Border Post" : `${item.name} ${item.surname}`}</h3>
        <div className="modal-actions">
          <button className="btn-secondary" onClick={() => onView(item)}>
            View Details
          </button>
          <button className="btn-secondary" onClick={() => onEdit(item)}>
            Edit
          </button>
          {type === "route" && (
            <>
              <button className="btn-secondary" onClick={() => onDuplicate(item)}>
                Duplicate
              </button>
              <button className="btn-secondary" onClick={() => onAssign(item, "border_posts")}>
                Assign Border Posts
              </button>
              <button className="btn-secondary" onClick={() => onAssign(item, "agents")}>
                Assign Agents
              </button>
              <button className="btn-secondary" onClick={() => onUnassign(item, "border_posts")}>
                Unassign Border Posts
              </button>
              <button className="btn-secondary" onClick={() => onUnassign(item, "agents")}>
                Unassign Agents
              </button>
              <button className="btn-secondary" onClick={() => onCostTemplate(item)}>
                📋 Cost Template
              </button>
            </>
          )}
          <button
            className="btn-delete"
            onClick={() => onDelete(item)}
            disabled={type === "route" && hasActiveTrips(item.id)}
            title={type === "route" && hasActiveTrips(item.id) ? "Cannot delete: Route has active trips" : ""}
          >
            Delete
          </button>
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const AssignModal = ({ item, field, options, selected, onSave, onClose }) => {
  const [selectedItems, setSelectedItems] = useState(selected || []);
  const [searchTerm, setSearchTerm] = useState("");
  const modalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  const toggleItem = (option) => {
    if (selectedItems.includes(option)) {
      setSelectedItems(selectedItems.filter((i) => i !== option));
    } else {
      setSelectedItems([...selectedItems, option]);
    }
  };

  const moveUp = (index) => {
    if (index > 0) {
      const newSelected = [...selectedItems];
      [newSelected[index - 1], newSelected[index]] = [newSelected[index], newSelected[index - 1]];
      setSelectedItems(newSelected);
    }
  };

  const moveDown = (index) => {
    if (index < selectedItems.length - 1) {
      const newSelected = [...selectedItems];
      [newSelected[index + 1], newSelected[index]] = [newSelected[index], newSelected[index + 1]];
      setSelectedItems(newSelected);
    }
  };

  const filteredOptions = options.filter(opt =>
    opt.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="modal-overlay">
      <div className="modal assign-modal" ref={modalRef} style={{ maxWidth: "700px" }}>
        <h3>Assign {field === "border_posts" ? "Border Posts" : "Agents"} to {item.name}</h3>
        <div className="assign-modal-grid">
          <div className="assign-column">
            <h4>Available {field === "border_posts" ? "Border Posts" : "Agents"}</h4>
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <div className="assign-options-list">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((opt) => (
                  <div
                    key={opt}
                    className={`assign-option ${selectedItems.includes(opt) ? "disabled" : ""}`}
                    onClick={() => !selectedItems.includes(opt) && toggleItem(opt)}
                  >
                    {opt}
                    {!selectedItems.includes(opt) && <span className="add-icon">+</span>}
                  </div>
                ))
              ) : (
                <p className="no-options">No options available</p>
              )}
            </div>
          </div>

          <div className="assign-column">
            <h4>Selected (in order)</h4>
            <div className="selected-items-list">
              {selectedItems.length > 0 ? (
                selectedItems.map((item, idx) => (
                  <div key={item} className="selected-item">
                    <span className="selected-item-name">{item}</span>
                    <div className="selected-item-controls">
                      <button
                        type="button"
                        onClick={() => moveUp(idx)}
                        disabled={idx === 0}
                        className="move-btn"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        onClick={() => moveDown(idx)}
                        disabled={idx === selectedItems.length - 1}
                        className="move-btn"
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleItem(item)}
                        className="remove-btn"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <p className="no-options">No items selected</p>
              )}
            </div>
          </div>
        </div>
        <div className="modal-actions">
          <button className="btn-primary" onClick={() => onSave(selectedItems)}>
            Save Order
          </button>
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default function RoutesPlanning() {
  const [activeTab, setActiveTab] = useState("routes");
  const [routes, setRoutes] = useState([]);
  const [borderPosts, setBorderPosts] = useState([]);
  const [agents, setAgents] = useState([]);
  const [trips, setTrips] = useState([]);
  const [costTemplates, setCostTemplates] = useState([]);
  const [routeForm, setRouteForm] = useState({
    name: "",
    points: [],
    total_distance_km: "",
    border_posts: [],
    agents: [],
    cost_template: null,
  });
  const [borderForm, setBorderForm] = useState({
    country: "",
    border_name: "",
    direction: "",
    entries: [],
  });
  const [agentForm, setAgentForm] = useState({
    name: "",
    surname: "",
    contact_phone: "",
    contact_email: "",
    border_post: "",
  });
  const [editingRouteId, setEditingRouteId] = useState(null);
  const [editingBorderId, setEditingBorderId] = useState(null);
  const [editingAgentId, setEditingAgentId] = useState(null);
  const [pointInput, setPointInput] = useState("");
  const [openDropdown, setOpenDropdown] = useState(null);
  const [actionModalItem, setActionModalItem] = useState(null);
  const [assignModal, setAssignModal] = useState(null);
  const [viewItem, setViewItem] = useState(null);
  const [viewBorderPosts, setViewBorderPosts] = useState(null);
  const [viewAgents, setViewAgents] = useState(null);
  const [costTemplateModal, setCostTemplateModal] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState(null);
  const [collapsedSections, setCollapsedSections] = useState({
    routesCreate: false,
    routesList: false,
    borderCreate: false,
    borderList: false,
    agentsCreate: false,
    agentsList: false,
  });
  const modalRef = useRef(null);
  const dropdownRef = useRef(null);

  // Get all border post options as flat array for dropdown - FIXED to return strings
  const getAllBorderPostOptions = () => {
    const options = [];
    borderPosts.forEach(bp => {
      if (bp.entries && Array.isArray(bp.entries)) {
        bp.entries.forEach(entry => {
          const option = `${entry.border_name} (${entry.country})`;
          if (!options.includes(option)) {
            options.push(option);
          }
        });
      }
    });
    return options;
  };

  useEffect(() => {
    async function loadAll() {
      setLoading(true);
      setFeedback(null);
      try {
        await Promise.all([loadRoutes(), loadBorderPosts(), loadAgents(), loadTrips(), loadCostTemplates()]);
        console.log("RoutesPlanning data loaded");
        console.log("Border post options:", getAllBorderPostOptions());
      } catch (error) {
        console.error("Error loading data:", error.code, error.message, error.stack);
        setFeedback({
          message: `Failed to load data: ${error.message}`,
          type: "error",
        });
      } finally {
        setLoading(false);
      }
    }
    loadAll();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        closeModals();
      }
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  async function loadRoutes() {
    try {
      const snaps = await getDocs(collection(db, "routes"));
      setRoutes(snaps.docs.map((d) => ({ id: d.id, ...d.data() })) || []);
    } catch (error) {
      console.error("Error fetching routes:", error.code, error.message, error.stack);
      setRoutes([]);
      throw error;
    }
  }

  async function loadBorderPosts() {
    try {
      const snaps = await getDocs(collection(db, "border_posts"));
      setBorderPosts(snaps.docs.map((d) => ({ id: d.id, ...d.data() })) || []);
    } catch (error) {
      console.error("Error fetching border posts:", error.code, error.message, error.stack);
      setBorderPosts([]);
      throw error;
    }
  }

  async function loadAgents() {
    try {
      const snaps = await getDocs(collection(db, "agents"));
      setAgents(snaps.docs.map((d) => ({ id: d.id, ...d.data() })) || []);
    } catch (error) {
      console.error("Error fetching agents:", error.code, error.message, error.stack);
      setAgents([]);
      throw error;
    }
  }

  async function loadTrips() {
    try {
      const snaps = await getDocs(collection(db, "trips"));
      setTrips(snaps.docs.map((d) => ({ id: d.id, ...d.data() })) || []);
    } catch (error) {
      console.error("Error fetching trips:", error.code, error.message, error.stack);
      setTrips([]);
      throw error;
    }
  }

  async function loadCostTemplates() {
    try {
      const snaps = await getDocs(collection(db, "clearances"));
      setCostTemplates(snaps.docs.map((d) => ({ id: d.id, ...d.data() })) || []);
    } catch (error) {
      console.error("Error fetching cost templates:", error.code, error.message, error.stack);
      setCostTemplates([]);
    }
  }

  function addPoint() {
    if (!pointInput.trim()) {
      setFeedback({ message: "Please enter a city name.", type: "error" });
      return;
    }
    setRouteForm({
      ...routeForm,
      points: [...(routeForm.points || []), pointInput.trim()],
    });
    setPointInput("");
  }

  function removePoint(index) {
    setRouteForm({
      ...routeForm,
      points: routeForm.points.filter((_, i) => i !== index),
    });
  }

  function movePointUp(index) {
    if (index > 0) {
      const newPoints = [...routeForm.points];
      [newPoints[index - 1], newPoints[index]] = [newPoints[index], newPoints[index - 1]];
      setRouteForm({ ...routeForm, points: newPoints });
    }
  }

  function movePointDown(index) {
    if (index < routeForm.points.length - 1) {
      const newPoints = [...routeForm.points];
      [newPoints[index + 1], newPoints[index]] = [newPoints[index], newPoints[index + 1]];
      setRouteForm({ ...routeForm, points: newPoints });
    }
  }

  function addBorderEntry() {
    const { country, border_name, direction } = borderForm;
    if (!country || !border_name || !direction) {
      setFeedback({ message: "Country, border name, and direction are required.", type: "error" });
      return;
    }
    setBorderForm({
      ...borderForm,
      entries: [...(borderForm.entries || []), { country, border_name, direction }],
      country: "",
      border_name: "",
      direction: "",
    });
  }

  function removeBorderEntry(index) {
    setBorderForm({
      ...borderForm,
      entries: borderForm.entries.filter((_, i) => i !== index),
    });
  }

  function moveBorderEntryUp(index) {
    if (index > 0) {
      const newEntries = [...borderForm.entries];
      [newEntries[index - 1], newEntries[index]] = [newEntries[index], newEntries[index - 1]];
      setBorderForm({ ...borderForm, entries: newEntries });
    }
  }

  function moveBorderEntryDown(index) {
    if (index < borderForm.entries.length - 1) {
      const newEntries = [...borderForm.entries];
      [newEntries[index + 1], newEntries[index]] = [newEntries[index], newEntries[index + 1]];
      setBorderForm({ ...borderForm, entries: newEntries });
    }
  }

  function toggleMultiSelect(field, option) {
    const current = routeForm[field] || [];
    if (current.includes(option)) {
      setRouteForm({ ...routeForm, [field]: current.filter((o) => o !== option) });
    } else {
      setRouteForm({ ...routeForm, [field]: [...current, option] });
    }
  }

  const renderDropdown = (label, field, options, multi = false) => {
    const isPlaceholder = !routeForm[field] || (Array.isArray(routeForm[field]) && routeForm[field].length === 0);
    return (
      <div className="dropdown-wrapper" ref={dropdownRef}>
        <div
          className={`select-control dropdown-input ${openDropdown === field ? "active" : ""} ${
            isPlaceholder ? "placeholder" : ""
          }`}
          onClick={() => setOpenDropdown(openDropdown === field ? null : field)}
        >
          {multi && routeForm[field]?.length > 0 ? (
            <div className="selected-tags">
              {routeForm[field].map((item) => (
                <span
                  key={item}
                  className="tag"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleMultiSelect(field, item);
                  }}
                >
                  {item} ✕
                </span>
              ))}
            </div>
          ) : isPlaceholder ? (
            `Select ${label}`
          ) : (
            routeForm[field]
          )}
        </div>
        {openDropdown === field && (
          <div className="dropdown">
            {options.length > 0 ? (
              options.map((opt) => (
                <div
                  key={opt}
                  className={`dropdown-option ${
                    (multi ? routeForm[field]?.includes(opt) : routeForm[field] === opt) ? "selected" : ""
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (multi) {
                      toggleMultiSelect(field, opt);
                    } else {
                      setRouteForm({ ...routeForm, [field]: opt });
                      setOpenDropdown(null);
                    }
                  }}
                >
                  {opt}
                </div>
              ))
            ) : (
              <div className="dropdown-option">No options available</div>
            )}
          </div>
        )}
      </div>
    );
  };

  async function handleRouteSubmit(e) {
    e.preventDefault();
    setFeedback(null);
    try {
      const { name, points, total_distance_km, border_posts, agents, cost_template } = routeForm;
      if (!name || !points?.length || !total_distance_km) {
        setFeedback({ message: "Name, at least one point, and total distance are required.", type: "error" });
        return;
      }
      if (Number(total_distance_km) <= 0) {
        setFeedback({ message: "Total distance must be positive.", type: "error" });
        return;
      }
      const payload = {
        name,
        points: points || [],
        total_distance_km: Number(total_distance_km),
        border_posts: border_posts || [],
        agents: agents || [],
        cost_template: cost_template || null,
      };
      if (editingRouteId) {
        await updateDoc(doc(db, "routes", editingRouteId), payload);
        setFeedback({ message: "Route updated successfully.", type: "success" });
        setEditingRouteId(null);
      } else {
        await addDoc(collection(db, "routes"), payload);
        setFeedback({ message: "Route added successfully.", type: "success" });
      }
      setRouteForm({ name: "", points: [], total_distance_km: "", border_posts: [], agents: [], cost_template: null });
      setPointInput("");
      await loadRoutes();
    } catch (error) {
      console.error("Error saving route:", error.code, error.message, error.stack);
      setFeedback({
        message: `Failed to save route: ${error.message}`,
        type: "error",
      });
    }
  }

  async function handleBorderSubmit(e) {
    e.preventDefault();
    setFeedback(null);
    try {
      if (!borderForm.entries?.length) {
        setFeedback({ message: "At least one border entry is required.", type: "error" });
        return;
      }
      const payload = { entries: borderForm.entries || [] };
      if (editingBorderId) {
        await updateDoc(doc(db, "border_posts", editingBorderId), payload);
        setFeedback({ message: "Border post updated successfully.", type: "success" });
        setEditingBorderId(null);
      } else {
        await addDoc(collection(db, "border_posts"), payload);
        setFeedback({ message: "Border post added successfully.", type: "success" });
      }
      setBorderForm({ country: "", border_name: "", direction: "", entries: [] });
      await loadBorderPosts();
    } catch (error) {
      console.error("Error saving border post:", error.code, error.message, error.stack);
      setFeedback({
        message: `Failed to save border post: ${error.message}`,
        type: "error",
      });
    }
  }

  async function handleAgentSubmit(e) {
    e.preventDefault();
    setFeedback(null);
    try {
      const { name, surname, contact_phone } = agentForm;
      if (!name || !surname || !contact_phone) {
        setFeedback({ message: "Name, surname, and phone are required.", type: "error" });
        return;
      }
      if (contact_phone && !/^\d{10}$/.test(contact_phone)) {
        setFeedback({ message: "Phone must be a 10-digit number.", type: "error" });
        return;
      }
      const payload = { ...agentForm };
      if (editingAgentId) {
        await updateDoc(doc(db, "agents", editingAgentId), payload);
        setFeedback({ message: "Agent updated successfully.", type: "success" });
        setEditingAgentId(null);
      } else {
        await addDoc(collection(db, "agents"), payload);
        setFeedback({ message: "Agent added successfully.", type: "success" });
      }
      setAgentForm({ name: "", surname: "", contact_phone: "", contact_email: "", border_post: "" });
      await loadAgents();
    } catch (error) {
      console.error("Error saving agent:", error.code, error.message, error.stack);
      setFeedback({
        message: `Failed to save agent: ${error.message}`,
        type: "error",
      });
    }
  }

  function confirmDeleteRoute(route) {
    setConfirmModal({
      title: "Delete Route",
      message: `Are you sure you want to delete route "${route.name}"? This action cannot be undone.`,
      confirmText: "Delete",
      confirmVariant: "danger",
      onConfirm: () => deleteRoute(route.id),
    });
    setActionModalItem(null);
  }

  function confirmDeleteBorder(border) {
    setConfirmModal({
      title: "Delete Border Post",
      message: `Are you sure you want to delete this border post? This action cannot be undone.`,
      confirmText: "Delete",
      confirmVariant: "danger",
      onConfirm: () => deleteBorder(border.id),
    });
    setActionModalItem(null);
  }

  function confirmDeleteAgent(agent) {
    setConfirmModal({
      title: "Delete Agent",
      message: `Are you sure you want to delete agent "${agent.name} ${agent.surname}"? This action cannot be undone.`,
      confirmText: "Delete",
      confirmVariant: "danger",
      onConfirm: () => deleteAgent(agent.id),
    });
    setActionModalItem(null);
  }

  async function deleteRoute(id) {
    setFeedback(null);
    try {
      await deleteDoc(doc(db, "routes", id));
      setFeedback({ message: "Route deleted successfully.", type: "success" });
      await loadRoutes();
    } catch (error) {
      console.error("Error deleting route:", error.code, error.message, error.stack);
      setFeedback({
        message: `Failed to delete route: ${error.message}`,
        type: "error",
      });
    }
  }

  async function deleteBorder(id) {
    setFeedback(null);
    try {
      await deleteDoc(doc(db, "border_posts", id));
      setFeedback({ message: "Border post deleted successfully.", type: "success" });
      await loadBorderPosts();
    } catch (error) {
      console.error("Error deleting border post:", error.code, error.message, error.stack);
      setFeedback({
        message: `Failed to delete border post: ${error.message}`,
        type: "error",
      });
    }
  }

  async function deleteAgent(id) {
    setFeedback(null);
    try {
      await deleteDoc(doc(db, "agents", id));
      setFeedback({ message: "Agent deleted successfully.", type: "success" });
      await loadAgents();
    } catch (error) {
      console.error("Error deleting agent:", error.code, error.message, error.stack);
      setFeedback({
        message: `Failed to delete agent: ${error.message}`,
        type: "error",
      });
    }
  }

  function editRoute(route) {
    setRouteForm({
      name: route.name || "",
      points: route.points || [],
      total_distance_km: route.total_distance_km ? String(route.total_distance_km) : "",
      border_posts: route.border_posts || [],
      agents: route.agents || [],
      cost_template: route.cost_template || null,
    });
    setEditingRouteId(route.id);
    setActionModalItem(null);
  }

  function editBorder(border) {
    setBorderForm({
      country: "",
      border_name: "",
      direction: "",
      entries: border.entries || [],
    });
    setEditingBorderId(border.id);
    setActionModalItem(null);
  }

  function editAgent(agent) {
    setAgentForm({
      name: agent.name || "",
      surname: agent.surname || "",
      contact_phone: agent.contact_phone || "",
      contact_email: agent.contact_email || "",
      border_post: agent.border_post || "",
    });
    setEditingAgentId(agent.id);
    setActionModalItem(null);
  }

  function duplicateRoute(route) {
    setRouteForm({
      name: `${route.name} (Copy)`,
      points: route.points || [],
      total_distance_km: route.total_distance_km ? String(route.total_distance_km) : "",
      border_posts: route.border_posts || [],
      agents: route.agents || [],
      cost_template: null,
    });
    setEditingRouteId(null);
    setActionModalItem(null);
  }

  function viewItemDetails(item, type) {
    setViewItem({ item, type });
    setActionModalItem(null);
  }

  function viewRouteBorderPosts(route) {
    setViewBorderPosts(route.border_posts || []);
    setActionModalItem(null);
  }

  function viewRouteAgents(route) {
    setViewAgents(route.agents || []);
    setActionModalItem(null);
  }

  function openCostTemplateModal(route) {
    setCostTemplateModal(route);
    setActionModalItem(null);
  }

  async function attachCostTemplate(routeId, templateName) {
    setFeedback(null);
    try {
      await updateDoc(doc(db, "routes", routeId), { cost_template: templateName });
      setFeedback({ message: templateName ? "Cost template attached successfully." : "Cost template removed.", type: "success" });
      setCostTemplateModal(null);
      await loadRoutes();
    } catch (error) {
      console.error("Error attaching cost template:", error);
      setFeedback({ message: `Failed to attach cost template: ${error.message}`, type: "error" });
    }
  }

  function assignItems(route, field) {
    setAssignModal({ route, field });
    setActionModalItem(null);
  }

  async function saveAssignedItems(routeId, field, selectedItems) {
    setFeedback(null);
    try {
      await updateDoc(doc(db, "routes", routeId), { [field]: selectedItems });
      setFeedback({ message: `Assigned ${field} successfully.`, type: "success" });
      setAssignModal(null);
      await loadRoutes();
    } catch (error) {
      console.error(`Error assigning ${field}:`, error.code, error.message, error.stack);
      setFeedback({
        message: `Failed to assign ${field}: ${error.message}`,
        type: "error",
      });
    }
  }

  async function unassignItems(route, field) {
    setFeedback(null);
    try {
      await updateDoc(doc(db, "routes", route.id), { [field]: [] });
      setFeedback({ message: `Unassigned ${field} successfully.`, type: "success" });
      setActionModalItem(null);
      await loadRoutes();
    } catch (error) {
      console.error(`Error unassigning ${field}:`, error.code, error.message, error.stack);
      setFeedback({
        message: `Failed to unassign ${field}: ${error.message}`,
        type: "error",
      });
    }
  }

  function hasActiveTrips(routeId) {
    return trips.some((t) => t.route_id === routeId && t.status === "Active");
  }

  function closeModals() {
    setEditingRouteId(null);
    setEditingBorderId(null);
    setEditingAgentId(null);
    setActionModalItem(null);
    setAssignModal(null);
    setViewItem(null);
    setViewBorderPosts(null);
    setViewAgents(null);
    setCostTemplateModal(null);
    setConfirmModal(null);
    setRouteForm({ name: "", points: [], total_distance_km: "", border_posts: [], agents: [], cost_template: null });
    setBorderForm({ country: "", border_name: "", direction: "", entries: [] });
    setAgentForm({ name: "", surname: "", contact_phone: "", contact_email: "", border_post: "" });
    setFeedback(null);
  }

  function toggleSection(section) {
    setCollapsedSections({ ...collapsedSections, [section]: !collapsedSections[section] });
  }

  return (
    <div className="routes-planning-page">
      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          {feedback && (
            <FeedbackModal
              message={feedback.message}
              type={feedback.type}
              onClose={() => setFeedback(null)}
            />
          )}
          <h2>Routes Planning & Management</h2>
          <div className="tabs">
            <button
              className={`btn-tab ${activeTab === "routes" ? "active" : ""}`}
              onClick={() => setActiveTab("routes")}
            >
              Routes
            </button>
            <button
              className={`btn-tab ${activeTab === "border" ? "active" : ""}`}
              onClick={() => setActiveTab("border")}
            >
              Border Posts
            </button>
            <button
              className={`btn-tab ${activeTab === "agents" ? "active" : ""}`}
              onClick={() => setActiveTab("agents")}
            >
              Agents
            </button>
          </div>
          {activeTab === "routes" && (
            <>
              <div className={`collapsible-section ${collapsedSections.routesCreate ? "collapsed" : "expanded"}`}>
                <div className="section-header" onClick={() => toggleSection("routesCreate")}>
                  <h3>Create Route</h3>
                  <span className="toggle-icon">{collapsedSections.routesCreate ? "+" : "−"}</span>
                </div>
                <div className={`section-content ${collapsedSections.routesCreate ? "collapsed" : "expanded"}`}>
                  <form onSubmit={handleRouteSubmit} className="form-grid routes-form-grid">
                    <div className="form-cell">
                      <input
                        placeholder="Route Name (e.g., Cape Town to Lusaka)"
                        value={routeForm.name}
                        onChange={(e) => setRouteForm({ ...routeForm, name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="form-cell">
                      {renderDropdown(
                        "Border Posts",
                        "border_posts",
                        getAllBorderPostOptions(),
                        true
                      )}
                    </div>
                    <div className="form-cell">
                      <div className="point-input">
                        <input
                          placeholder="Add city (e.g., Cape Town)"
                          value={pointInput}
                          onChange={(e) => setPointInput(e.target.value)}
                        />
                        <button type="button" className="btn-secondary" onClick={addPoint}>
                          Add Point
                        </button>
                      </div>
                      {routeForm.points?.length > 0 && (
                        <div className="points-list">
                          {routeForm.points.map((point, index) => (
                            <div key={index} className="point-item">
                              <span className="point-order">{index + 1}.</span>
                              <span className="point-name">{point}</span>
                              <div className="point-controls">
                                <button
                                  type="button"
                                  onClick={() => movePointUp(index)}
                                  disabled={index === 0}
                                  className="move-point-btn"
                                >
                                  ↑
                                </button>
                                <button
                                  type="button"
                                  onClick={() => movePointDown(index)}
                                  disabled={index === routeForm.points.length - 1}
                                  className="move-point-btn"
                                >
                                  ↓
                                </button>
                                <button
                                  type="button"
                                  className="btn-delete-small"
                                  onClick={() => removePoint(index)}
                                >
                                  ✕
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="form-cell">
                      {renderDropdown(
                        "Agents",
                        "agents",
                        agents.map((a) => `${a.name} ${a.surname}`) || [],
                        true
                      )}
                    </div>
                    <div className="form-cell">
                      <input
                        type="number"
                        placeholder="Total Distance (km)"
                        value={routeForm.total_distance_km}
                        onChange={(e) =>
                          setRouteForm({ ...routeForm, total_distance_km: e.target.value })
                        }
                        required
                      />
                    </div>
                    <div className="form-cell"></div>
                    <div className="form-actions">
                      <button type="submit" className="btn-primary">
                        {editingRouteId ? "Update Route" : "Add Route"}
                      </button>
                      {editingRouteId && (
                        <button
                          type="button"
                          className="btn-secondary"
                          onClick={closeModals}
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                    <div className="form-cell"></div>
                  </form>
                </div>
              </div>
              <div className={`collapsible-section ${collapsedSections.routesList ? "collapsed" : "expanded"}`}>
                <div className="section-header" onClick={() => toggleSection("routesList")}>
                  <h3>Routes List</h3>
                  <span className="toggle-icon">{collapsedSections.routesList ? "+" : "−"}</span>
                </div>
                <div className={`section-content ${collapsedSections.routesList ? "collapsed" : "expanded"}`}>
                  <div className="table-wrap">
                    <table className="simple-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Points</th>
                          <th>Distance (km)</th>
                          <th>Border Posts</th>
                          <th>Agents</th>
                          <th>Cost Template</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {routes.length === 0 ? (
                          <tr>
                            <td colSpan="7">No routes found</td>
                          </tr>
                        ) : (
                          routes.map((r) => (
                            <tr key={r.id}>
                              <td data-label="Name">{r.name || "-"}</td>
                              <td data-label="Points">
                                {r.points?.map((p, i) => `${i + 1}. ${p}`).join(" → ") || "-"}
                              </td>
                              <td data-label="Distance">{r.total_distance_km || "-"}</td>
                              <td data-label="Border Posts" className="view-cell">
                                <div className="view-cell-content">
                                  <span className="count-badge">{r.border_posts?.length || 0}</span>
                                  <button
                                    className="view-icon-btn"
                                    onClick={() => viewRouteBorderPosts(r)}
                                    disabled={!r.border_posts || r.border_posts.length === 0}
                                    title={r.border_posts?.length ? "View border posts" : "No border posts assigned"}
                                  >
                                    👁️
                                  </button>
                                </div>
                              </td>
                              <td data-label="Agents" className="view-cell">
                                <div className="view-cell-content">
                                  <span className="count-badge">{r.agents?.length || 0}</span>
                                  <button
                                    className="view-icon-btn"
                                    onClick={() => viewRouteAgents(r)}
                                    disabled={!r.agents || r.agents.length === 0}
                                    title={r.agents?.length ? "View agents" : "No agents assigned"}
                                  >
                                    👁️
                                  </button>
                                </div>
                              </td>
                              <td data-label="Cost Template">
                                {r.cost_template ? (
                                  <span className="template-badge-small">📋 {r.cost_template}</span>
                                ) : (
                                  <span className="no-template">—</span>
                                )}
                              </td>
                              <td className="actions-cell">
                                <button
                                  className="menu-button"
                                  onClick={() => setActionModalItem({ item: r, type: "route" })}
                                >
                                  ⋮
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </>
          )}
          {activeTab === "border" && (
            <>
              <div className={`collapsible-section ${collapsedSections.borderCreate ? "collapsed" : "expanded"}`}>
                <div className="section-header" onClick={() => toggleSection("borderCreate")}>
                  <h3>Create Border Post Sequence</h3>
                  <span className="toggle-icon">{collapsedSections.borderCreate ? "+" : "−"}</span>
                </div>
                <div className={`section-content ${collapsedSections.borderCreate ? "collapsed" : "expanded"}`}>
                  <form onSubmit={handleBorderSubmit} className="border-form">
                    <div className="border-input-row">
                      <select
                        value={borderForm.country}
                        onChange={(e) => setBorderForm({ ...borderForm, country: e.target.value, border_name: "" })}
                      >
                        <option value="">Select Country</option>
                        {sadcCountries?.map((country) => (
                          <option key={country.name} value={country.name}>
                            {country.name}
                          </option>
                        ))}
                      </select>
                      <select
                        value={borderForm.border_name}
                        onChange={(e) => setBorderForm({ ...borderForm, border_name: e.target.value })}
                        disabled={!borderForm.country}
                      >
                        <option value="">Select Border Post</option>
                        {borderForm.country &&
                          sadcCountries
                            ?.find((c) => c.name === borderForm.country)
                            ?.border_posts?.map((bp) => (
                              <option key={bp} value={bp}>
                                {bp}
                              </option>
                            ))}
                      </select>
                      <select
                        value={borderForm.direction}
                        onChange={(e) => setBorderForm({ ...borderForm, direction: e.target.value })}
                      >
                        <option value="">Direction</option>
                        <option value="Entry">Entry</option>
                        <option value="Exit">Exit</option>
                      </select>
                      <button type="button" className="btn-secondary" onClick={addBorderEntry}>
                        Add to Sequence
                      </button>
                    </div>
                    <div className="border-form-actions">
                      <button type="submit" className="btn-primary">
                        {editingBorderId ? "Update Border Sequence" : "Save Border Sequence"}
                      </button>
                      {editingBorderId && (
                        <button type="button" className="btn-secondary" onClick={closeModals}>
                          Cancel
                        </button>
                      )}
                    </div>
                    {borderForm.entries?.length > 0 && (
                      <div className="border-entries-list">
                        <h4>Border Post Sequence (in order)</h4>
                        {borderForm.entries.map((entry, index) => (
                          <div key={index} className="border-entry-item">
                            <span className="entry-order">{index + 1}.</span>
                            <span className="entry-name">
                              {entry.border_name} ({entry.country}, {entry.direction})
                            </span>
                            <div className="entry-controls">
                              <button
                                type="button"
                                onClick={() => moveBorderEntryUp(index)}
                                disabled={index === 0}
                                className="move-entry-btn"
                              >
                                ↑
                              </button>
                              <button
                                type="button"
                                onClick={() => moveBorderEntryDown(index)}
                                disabled={index === borderForm.entries.length - 1}
                                className="move-entry-btn"
                              >
                                ↓
                              </button>
                              <button
                                type="button"
                                className="btn-delete-small"
                                onClick={() => removeBorderEntry(index)}
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </form>
                </div>
              </div>
              <div className={`collapsible-section ${collapsedSections.borderList ? "collapsed" : "expanded"}`}>
                <div className="section-header" onClick={() => toggleSection("borderList")}>
                  <h3>Border Posts List</h3>
                  <span className="toggle-icon">{collapsedSections.borderList ? "+" : "−"}</span>
                </div>
                <div className={`section-content ${collapsedSections.borderList ? "collapsed" : "expanded"}`}>
                  <div className="table-wrap">
                    <table className="simple-table">
                      <thead>
                        <tr>
                          <th>Border Post Sequence</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {borderPosts.length === 0 ? (
                          <tr>
                            <td colSpan="2">No border posts found</td>
                          </tr>
                        ) : (
                          borderPosts.map((b) => (
                            <tr key={b.id}>
                              <td data-label="Sequence">
                                {b.entries
                                  ?.map((e, i) => `${i + 1}. ${e.border_name} (${e.country}, ${e.direction})`)
                                  .join(" → ") || "-"}
                              </td>
                              <td className="actions-cell">
                                <button
                                  className="menu-button"
                                  onClick={() => setActionModalItem({ item: b, type: "border" })}
                                >
                                  ⋮
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </>
          )}
          {activeTab === "agents" && (
            <>
              <div className={`collapsible-section ${collapsedSections.agentsCreate ? "collapsed" : "expanded"}`}>
                <div className="section-header" onClick={() => toggleSection("agentsCreate")}>
                  <h3>Create Agent</h3>
                  <span className="toggle-icon">{collapsedSections.agentsCreate ? "+" : "−"}</span>
                </div>
                <div className={`section-content ${collapsedSections.agentsCreate ? "collapsed" : "expanded"}`}>
                  <form onSubmit={handleAgentSubmit} className="form-grid agents-form-grid">
                    <div className="form-cell">
                      <input
                        placeholder="Name"
                        value={agentForm.name}
                        onChange={(e) => setAgentForm({ ...agentForm, name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="form-cell">
                      <input
                        placeholder="Surname"
                        value={agentForm.surname}
                        onChange={(e) => setAgentForm({ ...agentForm, surname: e.target.value })}
                        required
                      />
                    </div>
                    <div className="form-cell">
                      <input
                        placeholder="Phone (10 digits)"
                        value={agentForm.contact_phone}
                        onChange={(e) => setAgentForm({ ...agentForm, contact_phone: e.target.value })}
                        required
                      />
                    </div>
                    <div className="form-cell">
                      <input
                        placeholder="Email (optional)"
                        value={agentForm.contact_email}
                        onChange={(e) => setAgentForm({ ...agentForm, contact_email: e.target.value })}
                      />
                    </div>
                    <div className="form-cell">
                      {renderDropdown(
                        "Border Post",
                        "border_post",
                        getAllBorderPostOptions()
                      )}
                    </div>
                    <div className="form-cell"></div>
                    <div className="form-actions">
                      <button type="submit" className="btn-primary">
                        {editingAgentId ? "Update Agent" : "Add Agent"}
                      </button>
                      {editingAgentId && (
                        <button type="button" className="btn-secondary" onClick={closeModals}>
                          Cancel
                        </button>
                      )}
                    </div>
                    <div className="form-cell"></div>
                  </form>
                </div>
              </div>
              <div className={`collapsible-section ${collapsedSections.agentsList ? "collapsed" : "expanded"}`}>
                <div className="section-header" onClick={() => toggleSection("agentsList")}>
                  <h3>Agents List</h3>
                  <span className="toggle-icon">{collapsedSections.agentsList ? "+" : "−"}</span>
                </div>
                <div className={`section-content ${collapsedSections.agentsList ? "collapsed" : "expanded"}`}>
                  <div className="table-wrap">
                    <table className="simple-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Surname</th>
                          <th>Contact Phone</th>
                          <th>Contact Email</th>
                          <th>Border Post</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {agents.length === 0 ? (
                          <tr>
                            <td colSpan="6">No agents found</td>
                          </tr>
                        ) : (
                          agents.map((a) => (
                            <tr key={a.id}>
                              <td data-label="Name">{a.name || "-"}</td>
                              <td data-label="Surname">{a.surname || "-"}</td>
                              <td data-label="Phone">{a.contact_phone || "-"}</td>
                              <td data-label="Email">{a.contact_email || "-"}</td>
                              <td data-label="Border Post">{a.border_post || "-"}</td>
                              <td className="actions-cell">
                                <button
                                  className="menu-button"
                                  onClick={() => setActionModalItem({ item: a, type: "agent" })}
                                >
                                  ⋮
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </>
          )}
          {actionModalItem && (
            <ActionModal
              item={actionModalItem.item}
              type={actionModalItem.type}
              onEdit={(item) =>
                actionModalItem.type === "route"
                  ? editRoute(item)
                  : actionModalItem.type === "border"
                  ? editBorder(item)
                  : editAgent(item)
              }
              onDelete={(item) =>
                actionModalItem.type === "route"
                  ? confirmDeleteRoute(item)
                  : actionModalItem.type === "border"
                  ? confirmDeleteBorder(item)
                  : confirmDeleteAgent(item)
              }
              onView={(item) => viewItemDetails(item, actionModalItem.type)}
              onDuplicate={duplicateRoute}
              onAssign={assignItems}
              onUnassign={unassignItems}
              onCostTemplate={openCostTemplateModal}
              onClose={closeModals}
              hasActiveTrips={hasActiveTrips}
            />
          )}
          {assignModal && (
            <AssignModal
              item={assignModal.route}
              field={assignModal.field}
              options={
                assignModal.field === "border_posts"
                  ? getAllBorderPostOptions()
                  : agents.map((a) => `${a.name} ${a.surname}`) || []
              }
              selected={assignModal.route[assignModal.field] || []}
              onSave={(selectedItems) => saveAssignedItems(assignModal.route.id, assignModal.field, selectedItems)}
              onClose={closeModals}
            />
          )}
          {viewItem && (
            <div className="modal-overlay">
              <div className="modal" ref={modalRef}>
                <h3>{viewItem.type === "route" ? "Route Details" : viewItem.type === "border" ? "Border Post Details" : "Agent Details"}</h3>
                <div className="form-block">
                  {viewItem.type === "route" && (
                    <>
                      <div className="form-cell">
                        <p><strong>Name:</strong> {viewItem.item.name || "-"}</p>
                      </div>
                      <div className="form-cell">
                        <p><strong>Points (in order):</strong> {viewItem.item.points?.map((p, i) => `${i + 1}. ${p}`).join(" → ") || "-"}</p>
                      </div>
                      <div className="form-cell">
                        <p><strong>Total Distance (km):</strong> {viewItem.item.total_distance_km || "-"}</p>
                      </div>
                      <div className="form-cell">
                        <p><strong>Border Posts (in order):</strong> {viewItem.item.border_posts?.map((b, i) => `${i + 1}. ${b}`).join(" → ") || "-"}</p>
                      </div>
                      <div className="form-cell">
                        <p><strong>Agents:</strong> {viewItem.item.agents?.join(", ") || "-"}</p>
                      </div>
                    </>
                  )}
                  {viewItem.type === "border" && (
                    <>
                      <div className="form-cell">
                        <p><strong>Border Post Sequence:</strong></p>
                        <ol>
                          {viewItem.item.entries?.map((e, i) => (
                            <li key={i}>{e.border_name} ({e.country}, {e.direction})</li>
                          ))}
                        </ol>
                      </div>
                    </>
                  )}
                  {viewItem.type === "agent" && (
                    <>
                      <div className="form-cell">
                        <p><strong>Name:</strong> {viewItem.item.name || "-"}</p>
                      </div>
                      <div className="form-cell">
                        <p><strong>Surname:</strong> {viewItem.item.surname || "-"}</p>
                      </div>
                      <div className="form-cell">
                        <p><strong>Contact Phone:</strong> {viewItem.item.contact_phone || "-"}</p>
                      </div>
                      <div className="form-cell">
                        <p><strong>Contact Email:</strong> {viewItem.item.contact_email || "-"}</p>
                      </div>
                      <div className="form-cell">
                        <p><strong>Border Post:</strong> {viewItem.item.border_post || "-"}</p>
                      </div>
                    </>
                  )}
                </div>
                <div className="modal-actions">
                  <button className="btn-secondary" onClick={closeModals}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
          {viewBorderPosts && (
            <ViewBorderPostsModal
              borderPosts={viewBorderPosts}
              onClose={() => setViewBorderPosts(null)}
            />
          )}
          {viewAgents && (
            <ViewAgentsModal
              agents={viewAgents}
              onClose={() => setViewAgents(null)}
            />
          )}
          {costTemplateModal && (
            <CostTemplateModal
              route={costTemplateModal}
              costTemplates={costTemplates}
              onAttach={(templateName) => attachCostTemplate(costTemplateModal.id, templateName)}
              onClose={() => setCostTemplateModal(null)}
            />
          )}
          {confirmModal && (
            <ConfirmModal
              title={confirmModal.title}
              message={confirmModal.message}
              confirmText={confirmModal.confirmText}
              confirmVariant={confirmModal.confirmVariant}
              onConfirm={confirmModal.onConfirm}
              onClose={() => setConfirmModal(null)}
            />
          )}
        </>
      )}
    </div>
  );
}